import { useState } from 'react'
import AudioSettings from './AudioSettings'
import Bestiary from './Bestiary'
import { useL10n } from '../hooks/useL10n'
import { GlobalAudio } from '../../game/systems/AudioSystem'
import './SettingsModal.css'

interface MoveGuideStats {
  id: string
  key: string
  labelKey: string
  subKey?: string
  action: string
  damage: number
  axeCost: number
  iframes: boolean
  virtualBtn: string
}

const MOVES_GUIDE_DATA: Record<string, MoveGuideStats> = {
  martelo: {
    id: 'martelo',
    key: 'Z',
    labelKey: 'controls.martelo',
    action: 'martelo',
    damage: 10,
    axeCost: 0,
    iframes: false,
    virtualBtn: 'A (Chute Rápido)',
  },
  rasteira: {
    id: 'rasteira',
    key: '↓+Z',
    labelKey: 'controls.rasteira',
    action: 'rasteira',
    damage: 12,
    axeCost: 0,
    iframes: true,
    virtualBtn: '↓ + A (Rasteira)',
  },
  bencao: {
    id: 'bencao',
    key: 'X',
    labelKey: 'controls.bencao',
    action: 'bencao',
    damage: 18,
    axeCost: 0,
    iframes: false,
    virtualBtn: 'B (Chute Forte)',
  },
  esquiva: {
    id: 'esquiva',
    key: 'C',
    labelKey: 'controls.esquiva',
    action: 'esquiva',
    damage: 0,
    axeCost: 0,
    iframes: true,
    virtualBtn: 'X (Esquiva)',
  },
  au: {
    id: 'au',
    key: '↓+C',
    labelKey: 'controls.au',
    action: 'au',
    damage: 0,
    axeCost: 15,
    iframes: true,
    virtualBtn: '↓ + X (Aú)',
  },
  meialua: {
    id: 'meialua',
    key: 'Espaço',
    labelKey: 'controls.special',
    subKey: 'controls.special_sub',
    action: 'meialua',
    damage: 32,
    axeCost: 50,
    iframes: true,
    virtualBtn: 'Y (Especial)',
  },
}

interface SettingsModalProps {
  isOpen: boolean
  onClose: () => void
  healCharges: number
  currentSkin?: string
  onChangeSkin?: (skin: 'raiz' | 'dourado' | 'equilibrado' | 'instrutor') => void
  currentDifficulty?: 'easy' | 'medium' | 'hard'
  onChangeDifficulty?: (d: 'easy' | 'medium' | 'hard') => void
  isPremium?: boolean
  onOpenPremium?: () => void
}

export default function SettingsModal({
  isOpen,
  onClose,
  healCharges,
  currentSkin,
  onChangeSkin,
  currentDifficulty,
  onChangeDifficulty,
  isPremium,
  onOpenPremium,
}: SettingsModalProps) {
  const { t, locale, setLocale } = useL10n()
  const [selectedMove, setSelectedMove] = useState<string>('martelo')

  if (!isOpen) return null

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="settings-modal__header">
          <h2>{t('ui.settings')}</h2>
          <button className="settings-modal__close" onClick={onClose} aria-label={t('ui.close')}>
            ✕
          </button>
        </div>

        <div className="settings-modal__content">
          <section className="settings-section">
            <h3 className="settings-section__title">{t('ui.language')}</h3>
            <div className="language-row">
              <button 
                className={`language-pill ${locale === 'pt' ? 'active' : ''}`} 
                onClick={() => { setLocale('pt'); GlobalAudio.unloadUnusedMenuMusic('pt'); }}
              >
                <span className="language-flag">🇧🇷</span> Português
              </button>
              <button 
                className={`language-pill ${locale === 'en' ? 'active' : ''}`} 
                onClick={() => { setLocale('en'); GlobalAudio.unloadUnusedMenuMusic('en'); }}
              >
                <span className="language-flag">🇺🇸</span> English
              </button>
              <button 
                className={`language-pill ${locale === 'es' ? 'active' : ''}`} 
                onClick={() => { setLocale('es'); GlobalAudio.unloadUnusedMenuMusic('es'); }}
              >
                <span className="language-flag">🇪🇸</span> Español
              </button>
            </div>
          </section>

          <section className="settings-section">
            <h3 className="settings-section__title">{t('ui.audio')}</h3>
            <AudioSettings />
          </section>

          {onChangeDifficulty && (
            <section className="settings-section">
              <h3 className="settings-section__title">{t('difficulty.label')}</h3>
              <div className="language-row" role="radiogroup" aria-label={t('difficulty.label')}>
                {(['easy', 'medium', 'hard'] as const).map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={`language-pill ${currentDifficulty === d ? 'active' : ''}`}
                    aria-pressed={currentDifficulty === d}
                    onClick={() => onChangeDifficulty(d)}
                  >
                    {t(`difficulty.${d}`)}
                  </button>
                ))}
              </div>
              <p className="settings-section__hint">
                {t(`difficulty.${currentDifficulty ?? 'medium'}_desc`)}
              </p>
            </section>
          )}

          {onOpenPremium && (
            <section className="settings-section settings-premium-section">
              <h3 className="settings-section__title">{t('ui.premium')}</h3>
              {isPremium ? (
                <div className="settings-premium-active">
                  <span className="settings-premium-badge">{t('ui.premium_active')}</span>
                  <p>{t('ui.premium_thanks')}</p>
                </div>
              ) : (
                <button
                  type="button"
                  className="settings-premium-cta"
                  onClick={() => {
                    onClose()
                    onOpenPremium()
                  }}
                >
                  <img src="/assets/ui/icon_premium_seal.png" alt="Premium" className="settings-premium-cta__icon" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
                  <span className="settings-premium-cta__text">
                    <strong>{t('ui.remove_ads')}</strong>
                    <span>{t('ui.premium_cta')}</span>
                  </span>
                  <span className="settings-premium-cta__arrow">›</span>
                </button>
              )}
            </section>
          )}

          {onChangeSkin && (
            <section className="settings-section">
              <h3 className="settings-section__title">{t('ui.appearance')}</h3>
              <div className="skin-row" role="radiogroup">
                {(['raiz', 'dourado', 'equilibrado', 'instrutor'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="skin-pill"
                    aria-pressed={currentSkin === s}
                    onClick={() => onChangeSkin(s)}
                  >
                    {t(`skins.${s}_name`)}
                  </button>
                ))}
              </div>
              <div className="skin-preview">
                <div 
                  key={currentSkin}
                  className="skin-preview__sprite animate-fade-in"
                  style={{ 
                    backgroundImage: `url(/assets/sprites/player/${currentSkin}/player_idle.png)` 
                  }}
                />
                <div className="skin-story animate-slide-up">
                  <h4 className="skin-story__name">{t(`skins.${currentSkin}_name`).toUpperCase()}</h4>
                  <p className="skin-story__text">{t(`skins.${currentSkin}_desc`)}</p>
                </div>
              </div>
            </section>
          )}

          <section className="settings-section">
            <h3 className="settings-section__title">{t('ui.controls')}</h3>
            <div className="settings-controls-grid">
              <ControlItem 
                k="Z" 
                label={t('controls.martelo')} 
                interactive 
                selected={selectedMove === 'martelo'} 
                onClick={() => setSelectedMove('martelo')} 
              />
              <ControlItem 
                k="↓+Z" 
                label={t('controls.rasteira')} 
                interactive 
                selected={selectedMove === 'rasteira'} 
                onClick={() => setSelectedMove('rasteira')} 
              />
              <ControlItem 
                k="X" 
                label={t('controls.bencao')} 
                interactive 
                selected={selectedMove === 'bencao'} 
                onClick={() => setSelectedMove('bencao')} 
              />
              <ControlItem 
                k="C" 
                label={t('controls.esquiva')} 
                interactive 
                selected={selectedMove === 'esquiva'} 
                onClick={() => setSelectedMove('esquiva')} 
              />
              <ControlItem 
                k="↓+C" 
                label={t('controls.au')} 
                interactive 
                selected={selectedMove === 'au'} 
                onClick={() => setSelectedMove('au')} 
              />
              <ControlItem 
                k="Espaço" 
                label={t('controls.special')} 
                sub={t('controls.special_sub')} 
                interactive 
                selected={selectedMove === 'meialua'} 
                onClick={() => setSelectedMove('meialua')} 
              />
              <ControlItem k="Shift" label={t('controls.defesa')} sub={t('controls.defesa_sub')} />
              <ControlItem k="H" label={t('controls.cura')} sub={t('controls.cargas').replace('{c}', healCharges.toString())} />
              <ControlItem k="Esc" label={t('controls.pause')} />
            </div>

            {selectedMove && MOVES_GUIDE_DATA[selectedMove] && (() => {
              const m = MOVES_GUIDE_DATA[selectedMove]
              const spriteUrl = `/assets/sprites/player/${currentSkin || 'raiz'}/player_${m.action}.png`
              return (
                <div className="moves-guide-card">
                  <div className="moves-guide-card__visual">
                    <div className="moves-guide-card__grid-bg" />
                    <div 
                      key={`${currentSkin}_${selectedMove}`}
                      className="moves-guide-card__sprite animate-fade-in"
                      style={{ backgroundImage: `url(${spriteUrl})` }}
                    />
                  </div>
                  <div className="moves-guide-card__details animate-fade-in" key={selectedMove}>
                    <div className="moves-guide-card__header">
                      <h4>{t('moves_guide.title')}: {t(m.labelKey)}</h4>
                      <div className="moves-guide-card__inputs">
                        <span className="moves-guide-card__input-pill">
                          <strong>{t('moves_guide.keyboard')}:</strong> <kbd>{m.key}</kbd>
                        </span>
                        <span className="moves-guide-card__input-pill">
                          <strong>{t('moves_guide.virtual')}:</strong> {m.virtualBtn}
                        </span>
                      </div>
                    </div>
                    <p className="moves-guide-card__desc">
                      {t(`moves_guide.${m.id}_desc`)}
                    </p>
                    <div className="moves-guide-card__stats">
                      <div className="moves-guide-stat">
                        <span className="moves-guide-stat__label">{t('moves_guide.dmg')}</span>
                        <div className="moves-guide-stat__val-container">
                          <span className="moves-guide-stat__val">{m.damage}</span>
                          {m.damage > 0 && (
                            <div className="moves-guide-stat__bar">
                              <div className="moves-guide-stat__fill" style={{ width: `${(m.damage / 32) * 100}%` }} />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="moves-guide-stat">
                        <span className="moves-guide-stat__label">{t('moves_guide.axe')}</span>
                        <div className="moves-guide-stat__val-container">
                          <span className="moves-guide-stat__val">{m.axeCost}</span>
                          {m.axeCost > 0 && (
                            <div className="moves-guide-stat__bar">
                              <div className="moves-guide-stat__fill" style={{ width: `${(m.axeCost / 50) * 100}%`, background: 'linear-gradient(90deg, #00ffcc, #0099ff)' }} />
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="moves-guide-stat">
                        <span className="moves-guide-stat__label">{t('moves_guide.iframes')}</span>
                        <span className={`moves-guide-stat__badge ${m.iframes ? '' : 'no'}`}>
                          {m.iframes ? t('moves_guide.iframes_yes') : t('moves_guide.iframes_no')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })()}

            <div className="controls-help-container">
              <h4 className="controls-help-container__title">{t('controls.help_title')}</h4>
              <p className="controls-help-container__text">{t('controls.help_text')}</p>
            </div>
          </section>

          <section className="settings-section">
            <h3 className="settings-section__title">{t('ui.folklore')}</h3>
            <Bestiary />
          </section>

          <div className="settings-version">Capoeira Soul v{__APP_VERSION__}</div>
        </div>
      </div>
    </div>
  )
}

function ControlItem({ 
  k, 
  label, 
  sub, 
  onClick, 
  selected, 
  interactive 
}: { 
  k: string; 
  label: string; 
  sub?: string; 
  onClick?: () => void; 
  selected?: boolean; 
  interactive?: boolean; 
}) {
  return (
    <div 
      className={`control-item ${interactive ? 'interactive' : ''} ${selected ? 'selected' : ''}`}
      onClick={onClick}
    >
      <kbd className="control-item__key">{k}</kbd>
      <div className="control-item__info">
        <span className="control-item__label">{label}</span>
        {sub && <span className="control-item__sub">{sub}</span>}
      </div>
    </div>
  )
}
